import { isValidEmail } from 'client/user';
import { css } from 'emotion';
import queryString from 'query-string';
import React, { useState, useEffect, ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import serverRenderingUtils from 'helpers/serverRenderingUtils';
import { useScript } from 'hooks/useScript';
import { Button } from 'shared/Button/NewButton';
import { paths } from 'client/shared/Paths';
import { useT } from 'client/i18n/hooks/useT';
import { useLocale } from 'client/i18n/hooks/useLocale';
import { INTEGRATION_CONNECTION_MENU_MODE } from 'client/screens/IntegrationConnectionScreen/constants';
import { AccessibleErrorContainer } from '../AccessibleErrorContainer';
import { AppleIcon } from '../AppleIcon';
import { FacebookIcon } from '../FacebookIcon';
import { FieldInput } from '../FieldInput';
import { GoogleIcon } from '../GoogleIcon';
import OutboundLink from '../OutboundLink';
import { TwitterIcon } from '../TwitterIcon';
import events from './events';
import {
  ButtonWrapper,
  ErrorAlert,
  FieldWrapper,
  TextButton,
  LoginFormWrapper,
  SocialLoginContainer,
  SocialSignInBorderCSS,
} from './styles';

type FormData = {
  email: string;
  password: string;
};

type Props = {
  clickRequestResetPassword: () => void;
  error: string;
  onSubmit: (data: FormData) => Promise<void>;
  setIntegrationConnectionScreenStatus?: (
    value: INTEGRATION_CONNECTION_MENU_MODE
  ) => void;
  submitting: boolean;
  showHelpLinks?: boolean;
  showSocialLogin?: boolean;
  feedUrl: string;
  signupUrl: string;
  showSignUp?: boolean;
};

// Global var for the Sign In With Apple API
declare let AppleID: any;

function getAppleAuthError() {
  if (serverRenderingUtils.windowUndefined()) return null;
  const { authappleerror } = queryString.parse(window.location.search);
  return authappleerror;
}

function buildQueryString(params: { [key: string]: string }) {
  const queryParams = Object.entries(params).filter(([, value]) => !!value);
  const searchParamsString = new URLSearchParams(queryParams).toString();
  return searchParamsString ? `?${searchParamsString}` : '';
}

export function Login({
  onSubmit,
  setIntegrationConnectionScreenStatus,
  showHelpLinks = true,
  showSocialLogin = true,
  signupUrl,
  showSignUp = true,
  clickRequestResetPassword,
  feedUrl,
}: Props) {
  const t = useT();
  const { locale } = useLocale();
  const {
    handleSubmit,
    formState: { isSubmitting, errors },
    register,
  } = useForm();
  const [formError, setFormError] = useState<ReactNode>(null);
  const [appleIdHasLoaded, setAppleIdHasLoaded] = useState(false);

  const queryParams = {
    feedUrl: feedUrl ? encodeURIComponent(feedUrl) : '',
    locale,
  };

  const urlQuery = buildQueryString(queryParams);
  const authTwitterUrl = `/auth/twitter${urlQuery}`;
  const authFacebookUrl = `/login/facebook${urlQuery}`;
  const authGoogleUrl = `/login/google${urlQuery}`;
  const authAppleError = getAppleAuthError();

  useScript(
    'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
    () => {
      const redirectURI = new URL(
        '/auth/apple/callback',
        serverRenderingUtils.getBaseUrl()
      );
      AppleID.auth.init({
        clientId: 'com.anchorfminc.anchor.siwa',
        scope: 'name email',
        redirectURI: redirectURI.href,
        state: urlQuery ? JSON.stringify(queryParams) : '',
      });
      setAppleIdHasLoaded(true);
    }
  );

  useEffect(() => {
    if (authAppleError) {
      setFormError(
        t(
          'Login.LoginErrorMessage',
          'There was a problem logging you in. Please try again from this device.',
          'Log in error message'
        )
      );
    }
  }, [authAppleError]);

  const onValid = (data: FormData) => {
    setFormError(null);
    onSubmit(data).catch(({ message }) => {
      switch (message) {
        case '401':
          setFormError(
            t(
              'Login.InvalidErrorMessage',
              'Invalid username or password.',
              'Log in error message'
            )
          );
          break;
        case '403':
          setFormError(
            <span
              dangerouslySetInnerHTML={{
                __html: t(
                  'Login.AccountDisabledMessage',
                  "This account has been disabled. <a href={helpPageLink}>Contact us</a> if you'd like to enable it.",
                  'Log in error message',
                  { helpPageLink: paths.getHelpPage('home') }
                ),
              }}
            />
          );
          break;
        case '429':
          setFormError(
            t(
              'Login.ServerErrorMessage',
              'Please try again later.',
              'Log in error message'
            )
          );
          break;
        default:
          setFormError(
            t(
              'Login.GenericErrorMessage',
              'There was a problem logging you in. Please try again from this device.',
              'Log in error message'
            )
          );
      }
    });
  };

  const handleResetPassword = () => {
    if (setIntegrationConnectionScreenStatus) {
      setIntegrationConnectionScreenStatus('requestResetPassword');
    } else {
      clickRequestResetPassword();
    }
  };

  return (
    <LoginFormWrapper>
      <form
        onSubmit={handleSubmit(onValid)}
        id="LoginForm"
        method="POST"
        noValidate={true}
      >
        <FieldWrapper>
          <FieldInput
            {...register('email', {
              required: t(
                'Login.EmailFieldRequired',
                'Email is required',
                'Email field required message'
              ),
              validate: email =>
                isValidEmail(email) ||
                t(
                  'Login.EmailFieldInvalid',
                  'Email is invalid',
                  'Email field invalid message'
                ),
            })}
            error={errors.email}
            placeholder={t('Login.EmailFieldText', 'Email', 'Email field text')}
            type="email"
            aria-label={t('Login.EmailFieldText', 'Email', 'Email field text')}
          />
        </FieldWrapper>
        <FieldWrapper>
          <FieldInput
            {...register('password', {
              required: t(
                'Login.PasswordFieldRequired',
                'Password is required',
                'Password field required message'
              ),
            })}
            error={errors.password}
            placeholder={t(
              'Login.PasswordFieldText',
              'Password',
              'Password field text'
            )}
            type="password"
            aria-label={t(
              'Login.PasswordFieldText',
              'Password',
              'Password field text'
            )}
          />
        </FieldWrapper>
        {formError && (
          <AccessibleErrorContainer>
            <ErrorAlert>{formError}</ErrorAlert>
          </AccessibleErrorContainer>
        )}
        <ButtonWrapper>
          <Button
            className={css`
              width: 100%;
            `}
            isDisabled={isSubmitting}
            type="submit"
            color="purple"
          >
            {t('Button.Login', 'Log in', 'Log in button text')}
          </Button>
        </ButtonWrapper>
      </form>
      {showHelpLinks && (
        <TextButton onClick={handleResetPassword}>
          {t(
            'Login.RequestResetPasswordButtonLabel',
            'Forgot password?',
            'Reset password request button text'
          )}
        </TextButton>
      )}
      {showSocialLogin && (
        <div aria-label="socialLoginOptions">
          <p
            id="socialLoginOptions"
            className={css`
              margin-bottom: 12px;
            `}
          >
            {t(
              'Login.LoginWithSocial',
              'Or log in with:',
              'Log in with social media platforms message'
            )}
          </p>
          <SocialLoginContainer>
            <li>
              <OutboundLink
                onClick={events.trackSocialClick}
                to={authFacebookUrl}
                className={css`
                  ${SocialSignInBorderCSS}
                  color: #39579b;
                `}
                aria-label={t(
                  'Login.FacebookSignInLinkAriaLabel',
                  'Sign in with Facebook',
                  'Facebook sign in link screenreader information'
                )}
              >
                <FacebookIcon width={18} height={18} />
              </OutboundLink>
            </li>
            <li>
              <OutboundLink
                onClick={events.trackSocialClick}
                to={authGoogleUrl}
                className={css`
                  ${SocialSignInBorderCSS}
                  color: #8c8c8c;
                `}
                aria-label={t(
                  'Login.GoogleSignInLinkAriaLabel',
                  'Sign in with Google',
                  'Google sign in link screenreader information'
                )}
              >
                <GoogleIcon width={18} height={18} />
              </OutboundLink>
            </li>
            <li>
              <OutboundLink
                onClick={events.trackSocialClick}
                to={authTwitterUrl}
                className={css`
                  ${SocialSignInBorderCSS}
                  color: #50abf1;
                `}
                aria-label={t(
                  'Login.TwitterSignInLinkAriaLabel',
                  'Sign in with Twitter',
                  'Twitter sign in link screenreader information'
                )}
              >
                <TwitterIcon width={22} height={18} />
              </OutboundLink>
            </li>
            {appleIdHasLoaded && (
              <li>
                <button
                  onClick={() => {
                    events.trackSocialClick();
                    AppleID.auth.signIn();
                  }}
                  className={SocialSignInBorderCSS}
                  aria-label={t(
                    'Login.AppleSignInLinkAriaLabel',
                    'Sign in with Apple',
                    'Apple sign in link screenreader information'
                  )}
                >
                  <AppleIcon
                    style={{ marginTop: '-4px' }}
                    width={24}
                    height={24}
                  />
                </button>
              </li>
            )}
          </SocialLoginContainer>
        </div>
      )}
      {showHelpLinks && (
        <div
          className={css`
            text-align: center;
          `}
        >
          {showSignUp && (
            <p>
              {t(
                'Login.SignupMessage',
                "Don't have an account? {signupLink} instead.",
                'Login page sign up message',
                {
                  signupLink: (
                    <Link
                      to={signupUrl || `/signup${urlQuery}`}
                      key="login-signup-link"
                    >
                      {t('Link.Signup', 'Sign up', 'Sign up link text')}
                    </Link>
                  ),
                }
              )}
            </p>
          )}
        </div>
      )}
    </LoginFormWrapper>
  );
}
